import { clsx } from 'clsx'

/**
 * Shared visual language for CertShieldPro's custom architectural
 * illustrations (Stage 9.2). Every illustration is an inline, responsive SVG
 * that reads as a technical diagram — precise strokes, mono labels, restrained
 * palette (gray structure + indigo accent + semantic status only). No raster
 * assets; each renders well under 5KB.
 *
 * Palette tokens (kept in one place so all six illustrations stay consistent):
 */
export const ink = '#030712' // gray-950
export const structure = '#d1d5db' // gray-300 — edges, hairlines
export const structureSoft = '#e5e7eb' // gray-200
export const accent = '#4338ca' // indigo-700
export const accentSoft = '#6366f1' // indigo-500
export const surface = '#ffffff'
export const surfaceMuted = '#f9fafb' // gray-50
export const label = '#6b7280' // gray-500
export const pass = '#059669' // emerald-600
export const warn = '#d97706' // amber-600
export const fail = '#dc2626' // red-600

/** Wrapper: responsive SVG that scales to its container width. */
export function Illustration({
  viewBox,
  className,
  title,
  children,
}: {
  viewBox: string
  className?: string
  title: string
  children: React.ReactNode
}) {
  return (
    <svg
      viewBox={viewBox}
      className={clsx('h-auto w-full', className)}
      role="img"
      aria-label={title}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      {children}
    </svg>
  )
}

/** Mono caption text — the diagram-label voice. */
export function Mono({
  x,
  y,
  children,
  fill = label,
  size = 11,
  weight = 500,
  anchor = 'start',
}: {
  x: number
  y: number
  children: React.ReactNode
  fill?: string
  size?: number
  weight?: number
  anchor?: 'start' | 'middle' | 'end'
}) {
  return (
    <text
      x={x}
      y={y}
      fill={fill}
      fontSize={size}
      fontWeight={weight}
      textAnchor={anchor}
      fontFamily="'Geist Mono', ui-monospace, SFMono-Regular, monospace"
      letterSpacing="0.02em"
    >
      {children}
    </text>
  )
}

/** A rounded node card with a hairline ring. */
export function Node({
  x,
  y,
  w,
  h,
  active = false,
  children,
}: {
  x: number
  y: number
  w: number
  h: number
  active?: boolean
  children?: React.ReactNode
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={10}
        fill={active ? ink : surface}
        stroke={active ? ink : structure}
        strokeWidth={1.5}
      />
      {children}
    </g>
  )
}
